import { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { useParams } from 'react-router-dom';

import { useFlag } from '../../../services/launchDarkly';
import { platformContextService } from '../../../services/platformContext/PlatformContextService';
import { generalStore } from '../../../stores/generalStore';
import { SubscriptionUtils } from '../../../stores/subscription/utils';
import { useStores } from '../../../stores/useStores';
import { RouterProviderEnum } from '../../../types';
import { getIframeMaxHeight, getWindowHeight } from '../../../utils/payment-app.utils';

import { buildStorePackagesLadder } from './buildStorePackagesLadder';
import { canShowPriceSelectorForActiveEvent } from './canShowPriceSelectorForActiveEvent';
import { mapStorePackageToPaymentPackage } from './mapStorePackageToPaymentPackage';
import type { PaymentsProps } from './types';

export function useIFrameParams(
  props: PaymentsProps,
  payComSessionData?: { sessionId: string; clientSessionSecretKey: string } | null,
) {
  const { loyaltyProgramStore, appStore, userStore, eventTriggerStore, subscriptionStore } = useStores();
  const { gameId } = useParams();
  const { currentOrderPackage, selectedPackage } = appStore;

  const paymentRouter = useFlag<RouterProviderEnum>('payment_router', RouterProviderEnum.PAY_COM);
  const uiRedesignDialogs = useFlag<boolean>('ui_redesign_dialogs', false);
  const payComApplePayRetry = useFlag<boolean>('pay_com_apple_pay_retry', false);
  const payComGooglePayRetry = useFlag<boolean>('pay_com_google_pay_retry', false);
  const isPriceSelectorEnabled = useFlag<boolean>('order_summary_price_selector', false);

  return useMemo(() => {
    if (isEmpty(selectedPackage)) {
      return undefined;
    }
    const { packageId, price } = selectedPackage;

    const purchaseSource = sessionStorage.getItem('purchaseSource');
    const params = new URLSearchParams();
    const activeEventAllowedKeys = ['userEventTriggerId'] as const;
    const activeEventBenefitAllowedKeys = ['benefitType', 'key', 'offerId', 'offerType'] as const;
    const activeEventEventTriggerAllowedKeys = ['triggerKey', 'conditionsKey', 'eventTriggerId', 'department'] as const;

    const { platform, paymentEmitterPlatform } = platformContextService.getConfig();

    params.set('platform', platform);

    if (paymentEmitterPlatform) {
      params.set('paymentEmitterPlatform', paymentEmitterPlatform);
    }

    params.set('storeType', props.storeType);
    params.set('storeSegment', loyaltyProgramStore.currentTierKey);
    params.set('storeKey', appStore.currentStore?.key || '');

    params.set('packageId', `${packageId}`);
    params.set('amount', String(price));
    params.set('language', userStore.profile?.language || '');
    params.set('country', userStore.profile?.territory?.countryCode || '');
    params.set('currency', userStore.profile?.currency.code || '');
    params.set('paymentRouter', paymentRouter);
    params.set('ui_redesign_dialogs', String(uiRedesignDialogs));
    params.set('pay_com_apple_pay_retry', String(payComApplePayRetry));
    params.set('pay_com_google_pay_retry', String(payComGooglePayRetry));
    params.set('version', String(5));

    if (generalStore.appBootParams?.utm_promo) {
      params.set('promoted_method', generalStore.appBootParams.utm_promo);
    }

    // Deposit flow: the payments app opens on its own "Add cash" screen (skipping the
    // order summary) and mints the real package once the user confirms an amount.
    if (props.isDeposit) {
      params.set('packageType', 'deposit');
    }

    // Shipping flow: the payments app opens on the Crown Rips "Ship Card" screen with the
    // shipment context; the fee package is minted there once the quote is confirmed.
    if (props.shipping) {
      params.set('packageType', 'shipping');
      params.set('shipItemIds', props.shipping.providerItemIds.join(','));
      if (props.shipping.cardName) params.set('shipCardName', props.shipping.cardName);
      if (props.shipping.cardSubtitle) params.set('shipCardSubtitle', props.shipping.cardSubtitle);
      if (props.shipping.cardImageUrl) params.set('shipCardImage', props.shipping.cardImageUrl);
      if (props.shipping.sessionId) params.set('ripsSessionId', props.shipping.sessionId);
    }

    if (paymentRouter === RouterProviderEnum.PAY_COM && payComSessionData) {
      params.set('payComSessionId', payComSessionData.sessionId);
      params.set('payComClientSecret', payComSessionData.clientSessionSecretKey);
    }

    if (selectedPackage.subscriptionData) {
      const subscription = selectedPackage.subscriptionData;
      const quantity = subscription.quantity || 1;

      const currentSubscription = subscriptionStore.currentSubscription;
      const isUpgrade =
        currentSubscription?.status === 'active' &&
        currentSubscription.plan.id !== subscription.id &&
        subscription.type !== 'one-time';

      const packageInfo = {
        packageId: subscription.packageId,
        price: subscription.price * quantity,
        ccCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'GC') * quantity,
        sweepsCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'CASH') * quantity,
        ccCurrencySymbol: SubscriptionUtils.getCurrencySymbol(subscription.benefitPackage.packageBundles, 'GC'),
        sweepsCurrencySymbol: SubscriptionUtils.getCurrencySymbol(subscription.benefitPackage.packageBundles, 'CASH'),
        subscriptionType: subscription.type || 'subscription',
        billingCycle: subscription.cycleType,
        subscriptionName: subscription.name,
        quantity,
        ...(isUpgrade && {
          isUpgrade: true,
          payNowPrice: SubscriptionUtils.calculateUpgradeCharge(currentSubscription, subscription.price * quantity),
        }),
      };
      params.set('package', JSON.stringify(packageInfo));
      params.set('planId', subscription.id);
      params.set('packageType', 'subscription');
    } else {
      const triggeredBy = currentOrderPackage?.eventTriggerData?.activeEvent ? 'offer' : 'store';
      const purchasePackage = mapStorePackageToPaymentPackage(selectedPackage, triggeredBy);

      params.set('package', JSON.stringify(purchasePackage));

      if (isPriceSelectorEnabled) {
        const ladder = buildStorePackagesLadder(appStore.storePackages, Number(price));

        const showSelector =
          ladder !== undefined &&
          canShowPriceSelectorForActiveEvent(
            currentOrderPackage?.eventTriggerData?.activeEvent,
            ladder.map((p) => p.packageId),
          );

        if (showSelector) {
          if (import.meta.env.DEV) {
            const serialized = JSON.stringify(ladder);
            console.debug('[KRA-213] storePackages param length:', serialized.length, 'tiers:', ladder.length);
          }
          params.set('storePackages', JSON.stringify(ladder));
        }
      }
    }
    params.set('maxHeight', `${props.isDeposit ? getWindowHeight() : getIframeMaxHeight()}`);

    if (currentOrderPackage?.eventTriggerData && currentOrderPackage.eventTriggerData.activeEvent) {
      params.set(
        'eventTriggerData',
        JSON.stringify({
          activeEvent: {
            ...pick(currentOrderPackage.eventTriggerData.activeEvent, activeEventAllowedKeys),
            benefit: {
              ...pick(currentOrderPackage.eventTriggerData.activeEvent.benefit, activeEventBenefitAllowedKeys),
            },
            eventTrigger: {
              ...pick(
                currentOrderPackage.eventTriggerData.activeEvent.eventTrigger,
                activeEventEventTriggerAllowedKeys,
              ),
            },
          },
          // Forward the backend-evaluated flag (from the event-trigger response) so the
          // payment UI hides cash for offers while wsc is unavailable. Other methods unaffected.
          eventTriggerCashPurchaseEnabled:
            eventTriggerStore.lastEventTriggerResponse?.eventTriggerCashPurchaseEnabled ?? true,
        }),
      );

      params.set('packageType', 'offer');
    }

    if (currentOrderPackage?.eventTriggerData?.activeEvent?.benefit?.benefitType === 'road') {
      params.set('packageType', 'road');
    }

    if (gameId) {
      params.set('gameId', gameId);
    }

    if (purchaseSource) {
      params.set('purchaseSource', purchaseSource);
    }

    return params.toString();
  }, [
    currentOrderPackage,
    gameId,
    appStore.currentStore?.key,
    appStore.storePackages,
    loyaltyProgramStore.currentTierKey,
    props.storeType,
    props.isDeposit,
    props.shipping,
    selectedPackage,
    subscriptionStore.currentSubscription,
    userStore.profile?.language,
    userStore.profile?.territory?.countryCode,
    userStore.profile?.currency.code,
    eventTriggerStore.lastEventTriggerResponse?.eventTriggerCashPurchaseEnabled,
    paymentRouter,
    uiRedesignDialogs,
    payComApplePayRetry,
    payComGooglePayRetry,
    isPriceSelectorEnabled,
    payComSessionData,
  ]);
}
